"use client";

import { useState } from "react";
import Link from "next/link";
import { useSession } from "@/hooks/use-session";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Panel, PanelBody, PanelHeader, PanelTitle } from "@/components/ui/panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import type { CommunityTagSuggestion } from "@/lib/db/schema";

interface CommunityTagCardProps {
  suggestion: CommunityTagSuggestion & {
    suggestedByName?: string | null;
    suggestedByEmail?: string | null;
    moderatedByName?: string | null;
  };
  essenceTitle: string;
  upvotes: number;
  downvotes: number;
  userVote?: number | null;
}

export function CommunityTagCard({
  suggestion,
  essenceTitle,
  upvotes: initialUpvotes,
  downvotes: initialDownvotes,
  userVote: initialUserVote,
}: CommunityTagCardProps) {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const [upvotes, setUpvotes] = useState(initialUpvotes);
  const [downvotes, setDownvotes] = useState(initialDownvotes);
  const [userVote, setUserVote] = useState<number | null>(initialUserVote ?? null);

  const voteMutation = useMutation({
    mutationFn: (vote: 1 | -1) =>
      fetch(`/api/community/suggestions/${suggestion.id}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vote }),
      }),
    onSuccess: (_response, vote) => {
      if (userVote === vote) {
        // User clicked same vote again - should have been handled by removeVote
        return;
      }
      if (userVote === 1) setUpvotes((v) => v - 1);
      if (userVote === -1) setDownvotes((v) => v - 1);
      if (vote === 1) setUpvotes((v) => v + 1);
      if (vote === -1) setDownvotes((v) => v + 1);
      setUserVote(vote);
      queryClient.invalidateQueries({ queryKey: ["community-suggestions", suggestion.essenceId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to vote");
    },
  });

  const removeVoteMutation = useMutation({
    mutationFn: () => fetch(`/api/community/suggestions/${suggestion.id}/vote`, { method: "DELETE" }),
    onSuccess: () => {
      if (userVote === 1) setUpvotes((v) => v - 1);
      if (userVote === -1) setDownvotes((v) => v - 1);
      setUserVote(null);
      queryClient.invalidateQueries({ queryKey: ["community-suggestions", suggestion.essenceId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to remove vote");
    },
  });

  const handleVote = (vote: 1 | -1) => {
    if (!session) {
      toast.error("You must be logged in to vote");
      return;
    }
    if (userVote === vote) {
      removeVoteMutation.mutate();
    } else {
      voteMutation.mutate(vote);
    }
  };

  const score = upvotes - downvotes;
  const isPending = suggestion.status === "pending";

  return (
    <Panel>
      <PanelHeader>
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div>
            <PanelTitle className="text-base">
              <Link href={`/essences/${suggestion.essenceId}`} className="hover:underline">
                {essenceTitle}
              </Link>
            </PanelTitle>
            <p className="mt-1 text-sm text-ink-3">
              Suggested by {suggestion.suggestedByName ?? suggestion.suggestedByEmail ?? "unknown"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant={
                suggestion.status === "approved"
                  ? "success"
                  : suggestion.status === "rejected"
                    ? "danger"
                    : "warning"
              }
            >
              {suggestion.status}
            </Badge>
          </div>
        </div>
      </PanelHeader>
      <PanelBody>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Badge variant="neutral">{suggestion.kind}</Badge>
            <span className="font-medium">{suggestion.value}</span>
            {suggestion.originalValue && suggestion.originalValue !== suggestion.value && (
              <Badge
                variant="neutral"
                className="cursor-help text-xs"
                title={`Original: ${suggestion.originalValue}`}
              >
                edited
              </Badge>
            )}
          </div>
          {isPending && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 ${userVote === 1 ? "text-accent" : ""}`}
                onClick={() => handleVote(1)}
                disabled={!session}
              >
                <ThumbsUp className="mr-1 h-4 w-4" />
                {upvotes}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 ${userVote === -1 ? "text-danger" : ""}`}
                onClick={() => handleVote(-1)}
                disabled={!session}
              >
                <ThumbsDown className="mr-1 h-4 w-4" />
                {downvotes}
              </Button>
              <span className={`text-sm font-medium ${score > 0 ? "text-accent" : score < 0 ? "text-danger" : ""}`}>
                {score > 0 ? `+${score}` : score}
              </span>
            </div>
          )}
        </div>
      </PanelBody>
    </Panel>
  );
}
